// When the bus will reach a stop (docs/spec/transport.md slice 4). Two ways, best first:
//   1. From the bus's position: straight-line distance to the stop and between the stops before it,
//      times a road factor, at the bus's recent speed (kept between 15 and 45 km/h), plus a short
//      stop at each stop on the way. Needs a fresh position and the stops' positions.
//   2. From how late the bus is: the planned time of the stop plus the delay at the last stop the
//      bus reached (its first event there against that stop's planned time).
// Anything else has no estimate. Pure, so the worker, tests and screens agree.

export interface EtaStop { id: string; time: string | null; lat: number | null; lng: number | null }
export interface EtaFix { lat: number; lng: number; speed: number | null; at: number }

const ROAD = 1.35; // roads are longer than a straight line
const DWELL_MS = 40_000; // a stop on the way
const FRESH_MS = 120_000;
const SPEED_MIN = 15 / 3.6, SPEED_MAX = 45 / 3.6, SPEED_DEFAULT = 22 / 3.6; // m/s

/** Metres between two points (haversine). */
export function metres(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const r = 6_371_000, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** "07:45" on a local date in a time zone, as epoch ms. */
export function plannedAt(hhmm: string, date: string, tz: string): number {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  const guess = Date.parse(`${date}T${hhmm.padStart(5, "0")}:00Z`);
  // The zone's offset at that moment: what the wall clock reads there, minus UTC.
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(new Date(guess)).map((p) => [p.type, p.value]));
  const wall = Date.parse(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:00Z`);
  return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), h, m) - (wall - guess);
}

/**
 * Estimated arrival (epoch ms) at `stops[target]`, or null. `reached` is the index of the last stop
 * the bus has been at (-1 before the first); `reachedAt` when it first recorded something there.
 */
export function eta(o: {
  stops: EtaStop[]; target: number; reached: number; reachedAt: number | null;
  fix: EtaFix | null; date: string; tz: string; now: number;
}): number | null {
  const { stops, target, reached, fix, now } = o;
  if (target < 0 || target >= stops.length || target <= reached) return null;
  const route = stops.slice(reached + 1, target + 1);
  if (fix && now - fix.at <= FRESH_MS && route.every((s) => s.lat != null && s.lng != null)) {
    let d = 0;
    let from: { lat: number; lng: number } = fix;
    for (const s of route) { d += metres(from, { lat: s.lat!, lng: s.lng! }); from = { lat: s.lat!, lng: s.lng! }; }
    const v = fix.speed != null && fix.speed > 1 ? Math.min(SPEED_MAX, Math.max(SPEED_MIN, fix.speed)) : SPEED_DEFAULT;
    return now + (d * ROAD) / v * 1000 + (route.length - 1) * DWELL_MS;
  }
  const planned = stops[target]!.time;
  if (!planned) return null;
  const last = reached >= 0 ? stops[reached]! : null;
  const delay = last?.time && o.reachedAt != null ? o.reachedAt - plannedAt(last.time, o.date, o.tz) : 0;
  return Math.max(now, plannedAt(planned, o.date, o.tz) + Math.max(0, delay));
}

/** "about 6 min", "about 1 min", "now". */
export function inMinutes(at: number, now: number): string {
  const m = Math.round((at - now) / 60_000);
  return m <= 0 ? "now" : `about ${m} min`;
}
